import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { ConfigProvider } from 'antd';
import { Sidebar } from '../shared/components';
import RequireAuth from '../shared/auth/RequireAuth';
import { antdTheme } from './antdTheme';
import apolloClient from './apolloClient';

// Lazy load components for better code splitting
const LoginPage = React.lazy(() => import('../features/auth/pages/LoginPage'));
const ChangePasswordPage = React.lazy(() => import('../features/auth/pages/ChangePasswordPage'));
const PricingPage = React.lazy(() => import('../features/pricing/pages/PricingPage'));
const RoutesPage = React.lazy(() => import('../features/routes/pages/RoutesPage'));
const RouteDetailsPage = React.lazy(() => import('../features/routes/pages/RouteDetailsPage'));
const ShipCompaniesPage = React.lazy(() => import('../features/ship-company/pages/ShipCompaniesPage'));
const ShipCompanyDetailsPage = React.lazy(() => import('../features/ship-company/pages/ShipCompanyDetailsPage'));
const VesselsPage = React.lazy(() => import('../features/vessels/pages/VesselsPage'));
const VesselDetailsPage = React.lazy(() => import('../features/vessels/pages/VesselDetailsPage'));
const PortsPage = React.lazy(() => import('../features/ports/pages/PortsPage'));
const PortDetailsPage = React.lazy(() => import('../features/ports/pages/PortDetailsPage'));
const AgentsPage = React.lazy(() => import('../features/agents/pages/AgentsPage'));
const AgentDetailsPage = React.lazy(() => import('../features/agents/pages/AgentDetailsPage'));
const TruckCompaniesPage = React.lazy(() => import('../features/truck-company/pages/TruckCompaniesPage'));
const TruckCompanyDetailsPage = React.lazy(() => import('../features/truck-company/pages/TruckCompanyDetailsPage'));
const TransportPricingPage = React.lazy(() => import('../features/transport-pricing/pages/TransportPricingPage'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-[200px] text-base">
    Loading...
  </div>
);

const App: React.FC = () => {
  return (
    <ApolloProvider client={apolloClient}>
      <ConfigProvider theme={antdTheme}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Authenticated, but outside the app shell (no sidebar) so it's
                reachable while a forced password change is pending. */}
            <Route
              path="/change-password"
              element={
                <RequireAuth>
                  <ChangePasswordPage />
                </RequireAuth>
              }
            />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <div className="flex min-h-screen">
                    <Sidebar />
                    <main className="flex-1 p-6">
                      <Routes>
                      <Route path="/" element={<PricingPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/routes" element={<RoutesPage />} />
                      <Route path="/routes/:routeId" element={<RouteDetailsPage />} />
                      <Route path="/ship-companies" element={<ShipCompaniesPage />} />
                      <Route path="/ship-companies/:shipCompanyId" element={<ShipCompanyDetailsPage />} />
                      <Route path="/vessels" element={<VesselsPage />} />
                      <Route path="/vessels/:vesselId" element={<VesselDetailsPage />} />
                      <Route path="/ports" element={<PortsPage />} />
                      <Route path="/ports/:portId" element={<PortDetailsPage />} />
                      <Route path="/agents" element={<AgentsPage />} />
                      <Route path="/agents/:agentId" element={<AgentDetailsPage />} />
                      <Route path="/truck-companies" element={<TruckCompaniesPage />} />
                      <Route path="/truck-companies/:truckCompanyId" element={<TruckCompanyDetailsPage />} />
                      <Route path="/transport-pricing" element={<TransportPricingPage />} />
                      </Routes>
                    </main>
                  </div>
                </RequireAuth>
              }
            />
          </Routes>
        </Suspense>
      </ConfigProvider>
    </ApolloProvider>
  );
};

export default App;
